import { Row, Col, Input, message, Form } from "antd";
import { CustomFormItem } from "Components/Form/CustomFormItem";
import { useTranslation } from "react-i18next";
import { Button } from "Components/General/Button";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";
import { I18N_NS } from "Config/i18n";
import { ChangeEvent, useCallback, useState } from "react";

interface AddUserProps extends ModalComponentProps {
  addNewUser: (value: API_PrintJobRecipient) => void;
}

const defaultContact: API_PrintJobRecipient = {
  name: "",
  email: "",
  street: "",
  postalCode: "",
  company: "",
  location: "",
  external: true,
  id: Math.random() * 1000,
};

export const AddUser = (props: AddUserProps) => {
  const { t } = useTranslation(I18N_NS.PROJECT);
  const [userContact, setUserContact] = useState<API_PrintJobRecipient & { firstName?: string }>(defaultContact);
  const [formHasErrors, setFormHasErrors] = useState(false);
  const [form] = Form.useForm();

  const handleInputChange = useCallback(
    (field: string) => (e: ChangeEvent<HTMLInputElement>) => {
      setUserContact((prev) => ({ ...prev, [field]: e.target.value }));
    },
    []
  );

  const handleAddContact = () => {
    const isEmpty = Object.keys(userContact).some(
      (field: keyof API_PrintJobRecipient) => field !== "email" && !userContact[field]
    );

    if (isEmpty) {
      message.error(t("Edit all fields to continue"));
    } else {
      props.addNewUser(userContact);
      props.hideModal();
    }
  };

  const checkFormValid = () => {
    const hasErrors = form?.getFieldsError().some(({ errors }) => errors.length > 0);
    setFormHasErrors(hasErrors);
  };

  return (
    <>
      <h1>{t("Add other recipient")}</h1>
      <Form
        form={form}
        onValuesChange={() => {
          checkFormValid();
        }}
      >
        <Row align="middle" gutter={24}>
          <Col span={12}>
            <CustomFormItem
              label={t("Company")}
              name={"companyName"}
              fullWidth={true}
              rules={[
                {
                  max: 200,
                  message: t("Maximum length reached"),
                },
              ]}
              validateTrigger={["onChange", "onBlur"]}
            >
              <Input value={userContact.company} onChange={handleInputChange("company")} />
            </CustomFormItem>
          </Col>
          <Col span={12}>
            <CustomFormItem
              label={t("Contact person")}
              name={"name"}
              fullWidth={true}
              rules={[
                {
                  max: 200,
                  message: t("Maximum length reached"),
                },
              ]}
              validateTrigger={["onChange", "onBlur"]}
            >
              <Input value={userContact.name} onChange={handleInputChange("name")} />
            </CustomFormItem>
          </Col>
          <Col span={12}>
            <CustomFormItem
              label={t("Street")}
              name={"street"}
              fullWidth={true}
              rules={[
                {
                  max: 200,
                  message: t("Maximum length reached"),
                },
              ]}
              validateTrigger={["onChange", "onBlur"]}
            >
              <Input value={userContact.street} onChange={handleInputChange("street")} />
            </CustomFormItem>
          </Col>
          <Col span={12}>
            <CustomFormItem
              label={t("Post code")}
              name={"postalCode"}
              fullWidth={true}
              rules={[
                {
                  max: 20,
                  message: t("Maximum length reached"),
                },
              ]}
              validateTrigger={["onChange", "onBlur"]}
            >
              <Input value={userContact.postalCode} onChange={handleInputChange("postalCode")} />
            </CustomFormItem>
          </Col>
          <Col span={12}>
            <CustomFormItem
              label={t("City")}
              name={"city"}
              fullWidth={true}
              rules={[
                {
                  max: 200,
                  message: t("Maximum length reached"),
                },
              ]}
              validateTrigger={["onChange", "onBlur"]}
            >
              <Input value={userContact.location} onChange={handleInputChange("location")} />
            </CustomFormItem>
          </Col>
          <Col span={12}>
            <CustomFormItem
              label={t("Email")}
              name={"email"}
              fullWidth={true}
              rules={[
                {
                  max: 60,
                  message: t("Maximum length reached"),
                },
              ]}
              validateTrigger={["onChange", "onBlur"]}
            >
              <Input value={userContact.email} onChange={handleInputChange("email")} />
            </CustomFormItem>
          </Col>
        </Row>
        <Row align="middle" justify="end" gutter={24}>
          <Button type="primary" faIcon={["fas", "plus"]} onClick={handleAddContact} disabled={formHasErrors}>
            {t("Add")}
          </Button>
        </Row>
      </Form>
    </>
  );
};
